import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Pagination, RenderEmptyList, Spinner, TooltipCustom, WithRoleAccess } from "@services/ui-components";
import { Link, useNavigate, useParams } from "react-router-dom";
import { truncateText } from "../constants";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import {
    useGetTechMapServicesQuery,
    useUpdateTechMapServicesMutation,
} from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import { TechMapServicesFilter } from "../types";
import { CompanyAccess, ServiceProxy } from "@me-team/host/main/graphql/types";
import RenderCategoryName from "../RenderComponents/RenderCategoryName";
import { useToast } from "../../../hooks/useToast";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    setStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import StepScreenshotModal from "../../Joyride/WarehouseTutorial/Modals/StepScreenshotModal";
import { writeOffScreenshots } from "../../Joyride/WarehouseTutorial/constants";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface TechMapServicesTableProps {
    filters: TechMapServicesFilter;
}

const TechMapServicesTable: React.FC<TechMapServicesTableProps> = ({ filters }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { techMapId, branchId } = useParams();
    const canEditTechMapServices = useRoleAccess(CompanyAccess.TechMapServiceListEdit);
    const parentCheckboxRef = useRef<HTMLInputElement>(null);
    const [allChecked, setAllChecked] = useState(false);
    const [indeterminate, setIndeterminate] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const { showToast, toggleShowToast, setShowToast } = useToast();
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();
    const [currentStep, setCurrentStep] = useState(() => getCurrentStepFromLocalStorage());
    const tutorialStepsWithScreens = [16, 17, 18, 19];
    const [showStepScreenshotModal, setShowStepScreenshotModal] = useState<boolean>(false);
    const handleCloseStepScreenshotModal = () => setShowStepScreenshotModal(false);

    useScrollToTopOnChange(currentPage);

    useEffect(() => {
        if (tutorialStepsWithScreens.includes(startStep)) {
            setShowStepScreenshotModal(true);
        } else {
            setShowStepScreenshotModal(false);
        }
    }, [startStep]);
    const isFilterNotEmpty = Object.values(filters).some((value) => value !== "" && value !== null);

    const resetCurrentPage = () => {
        setCurrentPage(1);
    };
    useEffect(() => {
        resetCurrentPage();
    }, [filters?.search, filters?.categoryId]);

    const {
        data: techMapServicesData,
        loading: techMapServicesLoading,
        refetch: refetchTechMapServices,
    } = useGetTechMapServicesQuery({
        variables: {
            techMapId: +techMapId,
            page: currentPage,
            itemsPerPage: itemsPerPage,
            search: filters?.search ? filters?.search : null,
            categoryId: filters?.categoryId ? filters?.categoryId : null,
            companyId: +currentCompanyId,
            connect: true,
        },
        skip: !currentCompanyId,
        fetchPolicy: "network-only",
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const [updateServiceConnection] = useUpdateTechMapServicesMutation({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const isServiceConnected = (service: ServiceProxy) => {
        return service?.technologicalMapServiceRelations?.length > 0;
    };

    const company = techMapServicesData?.user?.company;
    const techMapServicesPaginated = company?.servicesListForTechMap;
    const techMapServices: ServiceProxy[] = techMapServicesPaginated?.services;
    const pageCount = parseInt(techMapServicesPaginated?.pageCount || "0");
    const totalItems = techMapServicesPaginated?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    useEffect(() => {
        if (techMapServices && parentCheckboxRef.current) {
            const all = techMapServices.every(isServiceConnected);
            const some = techMapServices.some(isServiceConnected);
            setAllChecked(all);
            setIndeterminate(!all && some);
        }
    }, [techMapServices]);

    useEffect(() => {
        if (parentCheckboxRef.current) {
            parentCheckboxRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    const handleParentCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!canEditTechMapServices) return;

        const isChecked = e.target.checked;
        const serviceIds = techMapServices.map((service) => service.id);
        const connect = indeterminate ? false : isChecked;

        await updateServiceConnection({
            variables: {
                techMapId: +techMapId,
                serviceIds,
                connect,
                branchId: Number(branchId),
            },
        });

        await refetchTechMapServices();
        toggleShowToast();
    };

    const handleServiceCheckboxChange = async (service: ServiceProxy) => {
        if (!canEditTechMapServices) return;

        await updateServiceConnection({
            variables: {
                techMapId: +techMapId,
                serviceIds: [service.id],
                connect: !isServiceConnected(service),
                branchId: Number(branchId),
            },
        });

        await refetchTechMapServices();
        toggleShowToast();
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const hasSelectedServices = techMapServices?.some(isServiceConnected) ?? false;

    if (techMapServicesLoading || !currentCompanyId) return <Spinner />;

    return (
        <>
            <div className="col-12 table-responsive scrollbar position-relative" data-tour="choose-service">
                {!!techMapServices?.length ? (
                    <table className="table table-borderless" data-tour="created-service">
                        <thead>
                            <tr className="fs-7">
                                {canEditTechMapServices ? (
                                    <th className="py-2 col-2 ps-4 pe-2 align-middle" style={{ minWidth: "106px" }}>
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Check
                                                type="checkbox"
                                                ref={parentCheckboxRef}
                                                checked={allChecked}
                                                onChange={handleParentCheckboxChange}
                                            />
                                            <span className="fs-7 text-dark d-flex align-items-center gap-2">
                                                {t("Choose Service")}
                                                <TooltipCustom
                                                    placement="bottom"
                                                    text={t(
                                                        "This technological map will be used for selected services"
                                                    )}
                                                >
                                                    <i className="bi bi-info-circle fs-7 text-dark d-flex align-items-center d-flex align-items-center"></i>
                                                </TooltipCustom>
                                            </span>
                                        </div>
                                    </th>
                                ) : null}
                                <th className="py-3 col-3 px-2 align-middle line-height-140">
                                    {t("ProductsList.Service")}
                                </th>
                                <th className="py-3 px-2 col-2 align-middle line-height-140">
                                    {t("ProductsList.Category")}
                                </th>
                                <th className="py-3 px-2 col-5 align-middle line-height-140">{t("Description")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {techMapServices.map((service) => (
                                <tr key={service.id} className="border-bottom">
                                    {canEditTechMapServices ? (
                                        <td className="align-middle ps-4 py-4 pe-2">
                                            <Form.Check
                                                type="checkbox"
                                                checked={isServiceConnected(service)}
                                                onChange={() => handleServiceCheckboxChange(service)}
                                            />
                                        </td>
                                    ) : null}
                                    <td className="align-middle py-4 px-2">
                                        <WithRoleAccess
                                            anyPermissions={[
                                                CompanyAccess.ServiceListView,
                                                CompanyAccess.ServiceAndOnlineBookingEdit,
                                            ]}
                                            fallback={<span>{service.name}</span>}
                                        >
                                            <Link
                                                to={`/services/service/${currentCompanyId}/service-list/${service.id}/edit/${branchId}`}
                                                className={`text-decoration-none text-primary`}
                                            >
                                                {service.name}
                                            </Link>
                                        </WithRoleAccess>
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <RenderCategoryName category={service.serviceCategoryNew} />
                                    </td>
                                    <td className="align-middle py-4 px-2">
                                        <span className="text-dark">{truncateText(service.description, 100)}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : null}
            </div>

            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : (
                <div data-tour="create-service-btn" className="mx-5 pb-4 mt-5">
                    <RenderEmptyList
                        isFilterNotEmpty={isFilterNotEmpty}
                        visibilityBtn={!isFilterNotEmpty}
                        emptyFilterTitle="No services were found for the specified values in the filters or search."
                        emptyListTitle="No services have been created yet. Add a service."
                        onClickButton={() => {
                            tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                            navigate(`/services/service/${currentCompanyId}/new/${branchId}`);
                        }}
                        buttonName={
                            <span>
                                <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                                {t("addService")}
                            </span>
                        }
                    />
                </div>
            )}

            {showStepScreenshotModal && (
                <StepScreenshotModal
                    key={currentStep}
                    show={showStepScreenshotModal}
                    onClose={handleCloseStepScreenshotModal}
                    step={currentStep}
                    screenshots={writeOffScreenshots}
                    dialogClassName={tutorialActive && "screenshot"}
                    centeredModal={false}
                    dataTour="screenshot-modal"
                />
            )}

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper
                    tutorialType={tutorialType}
                    startStep={startStep}
                    hasSelectedServices={hasSelectedServices}
                    goNext={() => {
                        const stepFromStorage = getCurrentStepFromLocalStorage();
                        setStepInLocalStorage(stepFromStorage);
                        setCurrentStep(stepFromStorage);
                        setShowStepScreenshotModal(true);
                    }}
                />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            <ToastComponent show={showToast} setShow={setShowToast} text={t("Saved")} />
        </>
    );
};

export default TechMapServicesTable;
